import axios from "axios";
import { toast } from "react-hot-toast";

const communicationServerApiBaseUrl = process.env.REACT_APP_TEMPLATES_API_URL

type GetCommunicationServerParams = {
  page?: number;
  pageSize?: number;
  search?: string;
};

type PagedCommunicationServerResponse<T = any> =
  | {
      data: T[];
      total: number;
      page: number;
      pageSize: number;
    }
  | T[];

export const getCommunicationServerRows = async (
  params?: GetCommunicationServerParams
): Promise<PagedCommunicationServerResponse> => {
  try {
    const response = await axios.get(`${communicationServerApiBaseUrl}/communication-server`, {
      params: {
        page: params?.page,
        pageSize: params?.pageSize,
        search: params?.search,
      },
    });
    return response.data;
  } catch (error: any) {
    toast.error(
      `Failed to fetch communication server rows: ${error?.response?.data?.message || error?.message || "Unknown error"}`
    );
    throw error;
  }
};

export const createCommunicationServerRow = async (payload: Record<string, any>) => {
  try {
    const response = await axios.post(`${communicationServerApiBaseUrl}/communication-server`, payload);
    return response.data;
  } catch (error: any) {
    toast.error(
      `Failed to create communication row: ${error?.response?.data?.message || error?.message || "Unknown error"}`
    );
    throw error;
  }
};

export const updateCommunicationServerRow = async (communicationId: string, payload: Record<string, any>) => {
  try {
    const response = await axios.patch(
      `${communicationServerApiBaseUrl}/communication-server/${encodeURIComponent(communicationId)}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    toast.error(
      `Failed to update communication row: ${error?.response?.data?.message || error?.message || "Unknown error"}`
    );
    throw error;
  }
};

/** Persist parsed recipient rows; backend may create/find a draft communication and return its id. */
/** Backend stores API-triggered communications as communicationKind `manual`. */
const kindForRecipientApi = (k: "cron" | "api"): "cron" | "manual" => (k === "api" ? "manual" : "cron");

export const uploadCommunicationRecipientData = async (payload: {
  columns: string[];
  rows: Record<string, string>[];
  /** When using separate CSVs per channel (e.g. whatsapp vs email) */
  channel?: string;
  /** Template used for this channel’s column set (cron add flow) */
  templateId?: string;
  /** Communication display name — used when creating a draft */
  communication: string;
  /** Same as `communication`; some APIs expect `name` */
  name?: string;
  /** Matches create payload: "cron" | "api" */
  communicationKind: "cron" | "api";
  /** After first upload, reuse the same draft for further channel uploads */
  communicationId?: string;
}): Promise<{
  batchId?: string;
  id?: string;
  rowCount?: number;
  communicationId?: string;
  communication_id?: string;
  recipientBatchId?: string;
  recipientDataId?: string;
}> => {
  try {
    const response = await axios.post(`${communicationServerApiBaseUrl}/communication-server/recipient-data`, {
      ...payload,
      communicationKind: kindForRecipientApi(payload.communicationKind),
    });
    return response.data ?? {};
  } catch (error: any) {
    toast.error(
      `Failed to upload recipient data: ${error?.response?.data?.message || error?.message || "Unknown error"}`
    );
    throw error;
  }
};

/** Load recipients from portal DB (candidates, users, or approaches) and create/update draft like CSV upload. */
export const importPortalRecipientData = async (payload: {
  source: "candidates" | "users" | "approaches";
  communication: string;
  name?: string;
  communicationKind: "cron" | "api";
  channel: string;
  templateId: string;
  communicationId?: string;
  limit?: number;
  skip?: number;
}): Promise<Record<string, unknown>> => {
  try {
    const response = await axios.post(
      `${communicationServerApiBaseUrl}/communication-server/recipient-data/portal`,
      {
        ...payload,
        communicationKind: kindForRecipientApi(payload.communicationKind),
      }
    );
    return response.data ?? {};
  } catch (error: any) {
    toast.error(
      `Failed to import from portal: ${error?.response?.data?.message || error?.response?.data?.error || error?.message || "Unknown error"}`
    );
    throw error;
  }
};

export type ObdPromptOption = {
  value: string;
  label: string;
  promptId: string;
  promptCategory?: string;
  fileName?: string;
  remarks?: string;
  promptDuration?: number;
  promptUrl?: string;
};

const normalizeObdPromptItems = (raw: unknown): ObdPromptOption[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      const promptId = String(item?.promptId ?? "").trim();
      if (!promptId) return null;
      const fileName = String(item?.fileName ?? "").trim();
      const remarks = String(item?.remarks ?? "").trim();
      const category = String(item?.promptCategory ?? "").trim();
      const duration =
        typeof item?.promptDuration === "number" && Number.isFinite(item.promptDuration)
          ? item.promptDuration
          : undefined;
      const labelParts = [
        fileName || `Prompt ${promptId}`,
        category ? `(${category})` : "",
        duration !== undefined ? `- ${duration}s` : "",
      ].filter(Boolean);
      return {
        value: promptId,
        label: labelParts.join(" ").trim(),
        promptId,
        promptCategory: category || undefined,
        fileName: fileName || undefined,
        remarks: remarks || undefined,
        promptDuration: duration,
        promptUrl: String(item?.promptUrl ?? "").trim() || undefined,
      } as ObdPromptOption;
    })
    .filter((x): x is ObdPromptOption => Boolean(x));
};

export const getObdPromptOptions = async (): Promise<ObdPromptOption[]> => {
  try {
    const response = await axios.get(`${communicationServerApiBaseUrl}/communication-server/voice-prompts`);
    const payload = response?.data;
    const items = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
    return normalizeObdPromptItems(items);
  } catch (error: any) {
    const message =
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      "Failed to fetch voice templates";
    toast.error(message);
    throw error;
  }
};
